// src/utils/auth.js

/**
 * Cookie Management Utilities
 */

export const getCookie = (name) => {
  if (typeof document === "undefined") return null; // Safety for SSR
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) return parts.pop().split(";").shift();
  return null;
};

export const setCookie = (name, value, days = 7) => {
  if (typeof document === "undefined") return;
  const expires = new Date(Date.now() + days * 24 * 60 * 60 * 1000).toUTCString();
  document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Strict`;
};

export const deleteCookie = (name) => {
  if (typeof document === "undefined") return;
  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`;
};

export const deleteAllCookies = () => {
  if (typeof document === "undefined") return;
  const cookies = document.cookie.split(";");
  for (let cookie of cookies) {
    const name = cookie.split("=")[0].trim();
    deleteCookie(name);
  }
};

/**
 * Authentication State Management
 */

export const saveAuthData = (user, userType, token) => {
  console.log('💾 Saving auth data:', { userType, businessType: user?.businessType });

  // Save token
  if (token) {
    localStorage.setItem('token', token);
    localStorage.setItem('auth_token', token);
    setCookie("auth_token", token, 7);
  }

  // CRITICAL FIX: Determine the correct user type to store
  let finalUserType = userType;

  // If user is a partner with businessType 'counselor', store as 'counselor'
  if (user?.businessType === 'counselor') {
    finalUserType = 'counselor';
    console.log('🎓 Partner has businessType counselor → Storing as counselor');
  }

  // Save user type
  localStorage.setItem('userType', finalUserType);
  localStorage.setItem('user_type', finalUserType);
  setCookie("user_type", finalUserType, 7);

  // Save user data
  if (user) {
    const userStr = JSON.stringify(user);
    localStorage.setItem("user", userStr);
    localStorage.setItem("user_data", userStr);
    
    // SAFETY: Cookies have a 4KB limit. If user data is too big, skip cookie storage to prevent crash.
    if (userStr.length < 3500) {
        setCookie("user_data", userStr, 7);
    } else {
        console.warn('⚠️ User data too large for cookie, stored in localStorage only');
    }
  }

  console.log('✅ Auth data saved:', { finalUserType, userId: user?.id || user?._id });
};

export const getAuthData = () => {
  if (typeof window === "undefined") return null;

  const authToken = localStorage.getItem('auth_token') || 
                    localStorage.getItem('token') || 
                    getCookie("auth_token");
  
  const userType = localStorage.getItem('user_type') || 
                   localStorage.getItem('userType') || 
                   getCookie("user_type");
  
  const userDataStr = localStorage.getItem("user_data") || 
                      localStorage.getItem("user") || 
                      getCookie("user_data");

  if (!authToken || !userType) {
    return null;
  }

  let userData = null;
  if (userDataStr && userDataStr !== "undefined") {
    try {
      userData = JSON.parse(userDataStr);
    } catch (e) {
      console.error('Error parsing user data:', e);
      // Don't return null here, just return empty object for user to prevent app crash
      userData = {}; 
    }
  }

  return {
    authToken,
    userType,
    userData,
  };
};

export const clearAuthData = () => {
  console.log('🧹 Clearing all auth data');
  
  // Clear cookies
  deleteCookie("auth_token");
  deleteCookie("user_type");
  deleteCookie("user_data");

  // Clear localStorage
  localStorage.removeItem("auth_token");
  localStorage.removeItem("user_type");
  localStorage.removeItem("user_data");
  localStorage.removeItem("user");
  localStorage.removeItem("userType");
  localStorage.removeItem("token");
  localStorage.removeItem("userId");
};

export const isAuthenticated = () => {
  const authToken = localStorage.getItem('auth_token') || 
                    localStorage.getItem('token') || 
                    getCookie("auth_token");
  
  const userType = localStorage.getItem('user_type') || 
                   localStorage.getItem('userType') || 
                   getCookie("user_type");
  
  return !!(authToken && userType);
};

// HELPER: Added for API calls that need just the token string
export const getToken = () => {
    return localStorage.getItem('auth_token') || 
           localStorage.getItem('token') || 
           getCookie("auth_token");
};

export const getUserType = () => {
  return localStorage.getItem('user_type') || 
         localStorage.getItem('userType') || 
         getCookie("user_type");
};

export const getUser = () => {
  const userDataStr = localStorage.getItem("user_data") || 
                      localStorage.getItem("user") || 
                      getCookie("user_data");
  
  if (!userDataStr || userDataStr === "undefined") return null;

  try {
    return JSON.parse(userDataStr);
  } catch (e) {
    console.error('Error parsing user data:', e);
    return null;
  }
};

/**
 * Helper to determine if user is counselor
 * CRITICAL: Checks multiple sources to identify counselors
 */
export const isCounselor = () => {
  const userType = getUserType();
  const userData = getUser();
  
  // Direct counselor type
  if (userType === 'counselor') {
    return true;
  }
  
  // Partner with businessType 'counselor'
  if (userType === 'partner' && userData?.businessType === 'counselor') {
    return true;
  }
  
  // Just checking userData alone
  if (userData?.businessType === 'counselor') {
    return true;
  }
  
  return false;
};

/**
 * Dashboard Redirect Helpers
 */

export const getDashboardLink = (userType, userData = null) => {
  // CRITICAL FIX: Check if user is counselor first
  if (isCounselor() || userType === 'counselor' || userData?.businessType === 'counselor') {
    return "/counselor/dashboard";
  }

  // Partner subtypes
  if (userType === 'partner') {
    if (userData?.businessType === 'agent') {
      return "/agent/dashboard";
    }
    if (userData?.businessType === 'institution') {
      return "/institution/dashboard";
    }
    // Default partner dashboard
    return "/partner/dashboard";
  }

  // Other user types
  switch (userType) {
    case "student":
      return "/student/dashboard";
    case "college":
      return "/college/dashboard";
    default:
      return "/dashboard";
  }
};

export const getInterestLink = (serviceInterest) => {
  switch (serviceInterest) {
    case "admission":
      return "/allcollege";
    case "counseling":
      return "/counseling";
    case "coaching_prep":
      return "/coaching";
    case "training_certification":
      return "/training";
    default:
      return "/allcollege";
  }
};

export const redirectToUserDashboard = () => {
  const authData = getAuthData();
  
  if (!authData) {
    window.location.href = "/login";
    return;
  }

  const { userType, userData } = authData;

  console.log('🔄 Redirecting to dashboard:', { userType, businessType: userData?.businessType });

  // Check if counselor (highest priority)
  if (isCounselor() || userType === 'counselor' || userData?.businessType === 'counselor') {
    console.log('➡️ Redirecting to counselor dashboard');
    window.location.href = '/counselor/dashboard';
    return;
  }

  // Handle student with service interest
  if (userType === "student" && userData?.serviceInterest) {
    console.log('➡️ Redirecting to student interest:', userData.serviceInterest);
    window.location.href = getInterestLink(userData.serviceInterest);
    return;
  }

  // Default dashboard redirect
  const dashboardLink = getDashboardLink(userType, userData);
  console.log('➡️ Redirecting to dashboard:', dashboardLink);
  window.location.href = dashboardLink;
};

/**
 * Auto-login Check (SAFE VERSION — NOW WITH ACTUAL REDIRECT)
 */

export const checkAndRedirectIfLoggedIn = (currentPath) => {
  const authData = getAuthData();

  if (!authData) return false;

  // FIX: If user IS logged in and is on a login/signup page, 
  // they SHOULD be redirected to dashboard. 
  // The previous code skipped this, leaving the user stuck on the login page.
  if (currentPath.includes("/login") || currentPath.includes("/signup")) {
    console.log('⚠️ User already logged in, redirecting to dashboard...');
    redirectToUserDashboard(); 
    return true; 
  }

  return false;
};

export default {
  getCookie,
  setCookie,
  deleteCookie,
  deleteAllCookies,
  saveAuthData,
  getAuthData,
  clearAuthData,
  isAuthenticated,
  getToken, // Exported new helper
  getUserType,
  getUser,
  getDashboardLink,
  getInterestLink,
  redirectToUserDashboard,
  checkAndRedirectIfLoggedIn,
  isCounselor,
};